import { ApolloClient, InMemoryCache, HttpLink } from "@apollo/client";
import { BLOCK_MAPPING } from "../constants/blocks.esm.js";
import { AUTHOR_QUERY } from "./queries/author.esm.js";
import { POSTS_QUERY, ARCHIVE_MORE_QUERY, SEARCH_QUERY, POST_QUERY, STANDALONE_PREVIEW_QUERY } from "./queries/post.esm.js";
import { TERM_QUERY } from "./queries/term.esm.js";
let apolloClient;
const getBlocksData = async (blocks) => Promise.all(blocks.map(async (block, index) => {
  var _a;
  try {
    if (BLOCK_MAPPING[block.blockName] && ((_a = BLOCK_MAPPING[block.blockName]) == null ? void 0 : _a.getBlockData)) {
      return {
        index,
        ...block,
        blockData: await BLOCK_MAPPING[block.blockName].getBlockData({
          index,
          ...block,
          innerBlocks: await getBlocksData(block.innerBlocks)
        })
      };
    }
    return {
      index,
      ...block,
      innerBlocks: await getBlocksData(block.innerBlocks)
    };
  } catch (error) {
    console.error(`[getBlocksData] Error processing block "${block.blockName}":`, error);
    return {
      index,
      ...block
    };
  }
}));
function createApolloClient() {
  const WP_GRAPHQL = process.env.WP_GRAPHQL || "";
  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    link: new HttpLink({
      uri: WP_GRAPHQL,
      // Server URL (must be absolute)
      credentials: "same-origin",
      // Additional fetch() options like `credentials` or `headers`
      headers: {
        "x-caller-id": "cms-blocks"
      }
    }),
    cache: new InMemoryCache()
  });
}
function initializeApollo() {
  const _apolloClient = apolloClient ?? createApolloClient();
  if (!apolloClient) {
    apolloClient = _apolloClient;
  }
  return _apolloClient;
}
getGraphQLData.ErrorCodes = {
  ZG_GRAPH_NOT_FOUND: "ZG_GRAPH_NOT_FOUND",
  ZG_GRAPH_INTERNAL_SERVER_ERROR: "ZG_GRAPH_INTERNAL_SERVER_ERROR",
  ZG_GRAPH_NO_DATA: "ZG_GRAPH_NO_DATA",
  ZG_GRAPH_PAGINATION_ERROR: "ZG_GRAPH_PAGINATION_ERROR"
};
async function getGraphQLData({
  query,
  variables,
  errorPolicy = "all",
  fetchPolicy
}) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const response = await apolloClient.query({
    query,
    variables,
    errorPolicy,
    fetchPolicy
  });
  if (((_a = response == null ? void 0 : response.errors) == null ? void 0 : _a.length) > 0) {
    if (((_b = response.errors[0].extensions) == null ? void 0 : _b.code) === "INTERNAL_SERVER_ERROR" && ((_d = (_c = response.errors[0].extensions) == null ? void 0 : _c.response) == null ? void 0 : _d.status) === 404) {
      console.error(`variables for ${getGraphQLData.ErrorCodes.ZG_GRAPH_NOT_FOUND}`, JSON.stringify(variables, null, 2));
      if (response.errors.length > 1) {
        console.error(`more than 404 error for ${getGraphQLData.ErrorCodes.ZG_GRAPH_NOT_FOUND}`, JSON.stringify(response.errors, null, 2));
      }
      return (response == null ? void 0 : response.data) || {};
    }
    if (((_i = (_h = (_g = (_f = (_e = response.errors) == null ? void 0 : _e[0]) == null ? void 0 : _f.extensions) == null ? void 0 : _g.response) == null ? void 0 : _h.body) == null ? void 0 : _i.code) === "rest_post_invalid_page_number") {
      throw new Error(getGraphQLData.ErrorCodes.ZG_GRAPH_PAGINATION_ERROR);
    }
    console.error(`response.errors for ${getGraphQLData.ErrorCodes.ZG_GRAPH_INTERNAL_SERVER_ERROR}`, JSON.stringify(response.errors, null, 2));
    console.error(`variables for ${getGraphQLData.ErrorCodes.ZG_GRAPH_INTERNAL_SERVER_ERROR}`, JSON.stringify(variables, null, 2));
    throw new Error(getGraphQLData.ErrorCodes.ZG_GRAPH_INTERNAL_SERVER_ERROR);
  }
  if (!(response == null ? void 0 : response.data)) {
    console.error(`variables for ${getGraphQLData.ErrorCodes.ZG_GRAPH_NO_DATA}`, JSON.stringify(variables, null, 2));
    throw new Error(getGraphQLData.ErrorCodes.ZG_GRAPH_NO_DATA);
  }
  return response.data;
}
async function getPost(options) {
  const {
    variables: {
      site = "ROOT",
      id,
      idType,
      postType = "PAGE",
      withRevisions = false,
      withSettings = false
    }
  } = options;
  const {
    cmsPost,
    cmsSettings
  } = await getGraphQLData({
    ...options,
    query: POST_QUERY,
    variables: {
      site,
      id,
      idType,
      postType,
      withRevisions,
      withSettings
    }
  });
  return {
    post: cmsPost,
    settings: cmsSettings
  };
}
async function getStandalonePreview(options) {
  const {
    variables: {
      site = "ROOT",
      id,
      idType = "ID",
      postType
    }
  } = options;
  const {
    cmsPost
  } = await getGraphQLData({
    ...options,
    query: STANDALONE_PREVIEW_QUERY,
    variables: {
      site,
      id,
      idType,
      postType
    }
  });
  return {
    component: cmsPost
  };
}
async function getPosts(options) {
  const {
    variables: {
      site = "ROOT",
      postType = "POST",
      perPage,
      page,
      orderby,
      order,
      search,
      taxonomy,
      withRevisions = false
    }
  } = options;
  const {
    cmsPosts
  } = await getGraphQLData({
    ...options,
    variables: {
      site,
      postType,
      perPage,
      page,
      orderby,
      order,
      search,
      taxonomy,
      withRevisions
    },
    query: POSTS_QUERY
  });
  return cmsPosts;
}
async function getArchiveMore(options) {
  const {
    variables: {
      site = "ROOT",
      perPage,
      page,
      orderby,
      order,
      search,
      taxonomies
    }
  } = options;
  const {
    cmsPosts
  } = await getGraphQLData({
    ...options,
    variables: {
      site,
      perPage,
      page,
      orderby,
      order,
      search,
      taxonomies
    },
    query: ARCHIVE_MORE_QUERY
  });
  return cmsPosts;
}
async function getSearchPosts(options) {
  const {
    variables: {
      site = "ROOT",
      perPage,
      page,
      search
    }
  } = options;
  const {
    cmsPosts,
    cmsSettings,
    cmsSearchHead
  } = await getGraphQLData({
    ...options,
    variables: {
      site,
      perPage,
      page,
      search
    },
    query: SEARCH_QUERY
  });
  return {
    posts: cmsPosts,
    settings: cmsSettings,
    head: cmsSearchHead == null ? void 0 : cmsSearchHead.head
  };
}
async function getTerm(options) {
  const {
    variables: {
      site = "ROOT",
      id,
      idType = "SLUG",
      taxonomy = "CATEGORY",
      termPostsType = "POST",
      termPostsPage = "1",
      termPostsPerPage = "10",
      withSettings = false
    }
  } = options;
  const {
    cmsTerm,
    cmsSettings
  } = await getGraphQLData({
    ...options,
    query: TERM_QUERY,
    variables: {
      site,
      id,
      idType,
      taxonomy,
      termPostsType,
      termPostsPage,
      termPostsPerPage,
      withSettings
    }
  });
  return {
    term: cmsTerm,
    settings: cmsSettings
  };
}
async function getAuthor(options) {
  const {
    variables: {
      site = "ROOT",
      id,
      idType = "SLUG",
      authorPostsType = "POST",
      authorPostsPage = "1",
      authorPostsPerPage = "12",
      withSettings = false
    }
  } = options;
  const {
    cmsAuthor,
    cmsSettings
  } = await getGraphQLData({
    ...options,
    query: AUTHOR_QUERY,
    variables: {
      site,
      id,
      idType,
      authorPostsType,
      authorPostsPage,
      authorPostsPerPage,
      withSettings
    }
  });
  return {
    author: cmsAuthor,
    settings: cmsSettings
  };
}
export {
  createApolloClient,
  getArchiveMore,
  getAuthor,
  getBlocksData,
  getGraphQLData,
  getPost,
  getPosts,
  getSearchPosts,
  getStandalonePreview,
  getTerm,
  initializeApollo
};
